import { useCallback, type AnimationEvent, type KeyboardEvent } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import IconButton from 'LuxKit/Button/IconButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import Group from 'components/utils/Group'
import { fadeOut } from 'components/utils/animations'
import type { TextDirection } from 'lib/dom/logicalScroll'
import { usePromptPosition, type PromptPositionOptions } from './hooks/usePromptPosition'
import PromptCard from './PromptCard'
import PromptTail from './PromptTail'

const CLOSE_ICON = <LineTimesIcon />
// Mic sits ~14px inside the composer; design gap below it is ~10px.
const MIC_PROMPT_POSITION: PromptPositionOptions = { placement: 'bottom-end', offset: 24 }

const messages = defineMessages({
  title: {
    id: 'Awl1gV1O1BDrDyyKV960',
    defaultMessage: 'Try planning by voice',
    description: 'Title of the tip under the TripPlanner landing page microphone button inviting travellers to speak instead of type',
  },
  body: {
    id: 'SSqjfmSYSey-H4wlugRr',
    defaultMessage: 'Just talk through your trip — who’s going, dates, what you love.',
    description: 'Body of the tip under the TripPlanner landing page microphone button suggesting what to say when planning by voice',
  },
  accept: {
    id: '8O0cgtQ1CnWzlrGh8OA5',
    defaultMessage: 'Try it now',
    description: 'Button in the TripPlanner landing page voice tip that immediately starts recording a voice message',
  },
  close: {
    id: 'jW7-uzgBGK-QotVCjCoO',
    defaultMessage: 'Close voice tip',
    description: 'Accessibility label for the close button on the TripPlanner landing page tip inviting travellers to plan by voice',
  },
  dismiss: {
    id: 'CnPsGIPo3GW61KfI0P6z',
    defaultMessage: 'Got it',
    description: 'Button in the TripPlanner landing page voice tip that closes the tip and snoozes voice prompts without recording',
  },
})

const Card = styled(PromptCard)`
  inline-size: ${rem(290)};

  &.closing {
    animation: ${fadeOut} 0.2s ease-in forwards;
  }
`

interface Props {
  anchorElement: HTMLElement | null
  textDirection: TextDirection
  isClosing: boolean
  onAccept: () => void
  onDismiss: () => void
  onClosed: () => void
}

function VoiceLandingTip({ anchorElement, textDirection, isClosing, onAccept, onDismiss, onClosed }: Props) {
  const intl = useIntl()
  const { setFloating, floatingStyle, arrowRef, arrowStyle, side, isVisible } = usePromptPosition(anchorElement, textDirection, MIC_PROMPT_POSITION)

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') onDismiss()
  }, [onDismiss])

  const handleAnimationEnd = useCallback((event: AnimationEvent<HTMLDivElement>) => {
    if (isClosing && event.target === event.currentTarget) onClosed()
  }, [isClosing, onClosed])

  return (
    <Card
      ref={setFloating}
      style={floatingStyle}
      className={clsx({ closing: isClosing, visible: isVisible })}
      background="normal-background"
      border="light-foreground"
      borderRadius="M"
      shadow="bottom-medium"
      padding={16}
      onKeyDown={handleKeyDown}
      onAnimationEnd={handleAnimationEnd}
    >
      <PromptTail ref={arrowRef} side={side} style={arrowStyle} />
      <Group direction="vertical" gap={12}>
        <Group direction="vertical" gap={4}>
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={8}>
            <BodyText variant="large" weight="bold">
              {intl.formatMessage(messages.title)}
            </BodyText>
            <IconButton
              kind="tertiary"
              size="small"
              shape="circle"
              horizontalOutdent="end"
              onClick={onDismiss}
              aria-label={intl.formatMessage(messages.close)}
            >
              {CLOSE_ICON}
            </IconButton>
          </Group>
          <BodyText variant="medium" colour="neutral-two">
            {intl.formatMessage(messages.body)}
          </BodyText>
        </Group>
        <Group direction="horizontal" gap={8}>
          <TextButton kind="primary" size="small" shape="round" onClick={onAccept}>
            {intl.formatMessage(messages.accept)}
          </TextButton>
          <TextButton kind="secondary" size="small" shape="round" onClick={onDismiss}>
            {intl.formatMessage(messages.dismiss)}
          </TextButton>
        </Group>
      </Group>
    </Card>
  )
}

export default VoiceLandingTip
